import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Target, Clock, Calendar, Award, Activity } from 'lucide-react';

interface UserTicketStats {
  userId: string;
  closedTickets: number;
  lastClosedAt: Date;
  ticketIds: string[];
  workflows: string[];
}

interface UserTicketStatsProps {
  userId?: string;
  className?: string;
}

const UserTicketStatsComponent: React.FC<UserTicketStatsProps> = ({ 
  userId, 
  className = "" 
}) => {
  const [stats, setStats] = useState<UserTicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserStats = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`/api/metrics/ticket-fix/users/${userId}`);
        
        if (response.ok) {
          const data = await response.json();
          setStats({
            ...data,
            lastClosedAt: new Date(data.lastClosedAt)
          });
        } else if (response.status === 403) {
          setError('Access denied. You can only view your own metrics.');
        } else {
          setError('Failed to load ticket fix statistics');
        }
      } catch (err) {
        console.error('Failed to fetch user ticket stats:', err);
        setError('Failed to load ticket fix statistics');
        setStats({
          userId: userId || 'unknown',
          closedTickets: 0,
          lastClosedAt: new Date(0),
          ticketIds: [],
          workflows: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserStats();
  }, [userId]);

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ElementType;
    color?: 'blue' | 'green' | 'purple' | 'yellow';
  }> = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'from-blue-500/20 to-cyan-500/20 border-blue-500/30 text-blue-400',
      green: 'from-green-500/20 to-emerald-500/20 border-green-500/30 text-green-400',
      purple: 'from-purple-500/20 to-pink-500/20 border-purple-500/30 text-purple-400',
      yellow: 'from-yellow-500/20 to-amber-500/20 border-yellow-500/30 text-yellow-400'
    };

    return (
      <div className={`glass-dark bg-gradient-to-br ${colorClasses[color]} backdrop-blur-xl border rounded-lg p-4`}>
        <div className="flex items-center justify-between mb-2">
          <Icon className="w-5 h-5 text-slate-300" />
        </div>
        <div className="text-2xl font-bold text-white mb-1">{value}</div>
        <div className="text-sm text-slate-400">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className}`}>
        <div className="glass-dark bg-gradient-to-br from-red-500/10 to-orange-500/10 backdrop-blur-xl border border-red-500/20 rounded-lg p-4">
          <div className="text-red-400 text-sm text-center">{error}</div>
        </div>
      </div>
    );
  }

  if (!stats || !userId) {
    return (
      <div className={`${className}`}>
        <div className="glass-dark bg-gradient-to-br from-slate-500/10 to-gray-500/10 backdrop-blur-xl border border-slate-500/20 rounded-lg p-4">
          <div className="text-slate-400 text-sm text-center">No ticket fix data available</div>
        </div>
      </div>
    );
  }

  const hasActivity = stats.closedTickets > 0;
  const daysSinceLastActivity = hasActivity 
    ? Math.floor((new Date().getTime() - stats.lastClosedAt.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className={`${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-white">Your Ticket Fix Stats</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard
            title="Tickets Closed"
            value={stats.closedTickets}
            icon={Target}
            color="green"
          />
          <StatCard
            title="Workflows Completed"
            value={stats.workflows.length}
            icon={Activity}
            color="blue"
          />
        </div>

        {hasActivity && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard
              title="Last Activity"
              value={stats.lastClosedAt.toLocaleDateString()}
              subtitle={stats.lastClosedAt.toLocaleTimeString()}
              icon={Calendar}
              color="purple"
            />
            <StatCard
              title="Days Since Last Fix"
              value={daysSinceLastActivity || 0}
              subtitle={daysSinceLastActivity === 0 ? "Today!" : daysSinceLastActivity === 1 ? "Yesterday" : "days ago"}
              icon={Clock}
              color="yellow"
            />
          </div>
        )}

        {hasActivity && stats.ticketIds.length > 0 && (
          <div className="glass-dark bg-gradient-to-br from-slate-500/10 to-gray-500/10 backdrop-blur-xl border border-slate-500/20 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Award className="w-4 h-4" />
              Recent Tickets Fixed
            </h4>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {stats.ticketIds.slice(0, 5).map((ticketId, index) => (
                <div key={ticketId} className="flex items-center justify-between p-2 bg-slate-800/50 rounded text-sm">
                  <span className="text-slate-300 font-mono">{ticketId}</span>
                  <span className="text-xs text-slate-500">#{index + 1}</span>
                </div>
              ))}
              {stats.ticketIds.length > 5 && (
                <div className="text-xs text-slate-400 text-center pt-2">
                  +{stats.ticketIds.length - 5} more tickets
                </div>
              )}
            </div>
          </div>
        )}

        {!hasActivity && (
          <div className="glass-dark bg-gradient-to-br from-slate-500/10 to-gray-500/10 backdrop-blur-xl border border-slate-500/20 rounded-lg p-6 text-center">
            <Target className="w-8 h-8 text-slate-400 mx-auto mb-3" />
            <div className="text-slate-400 text-sm mb-2">No tickets fixed yet</div>
            <div className="text-xs text-slate-500">
              Complete your first ticket fix workflow to see your stats here
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default UserTicketStatsComponent;
